'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import moment from 'moment';
import uuid from 'uuid';

import MealPreviewCard from '../../Plans/Editor/grid/MealPreviewCard.react';
import { AssetsProvider } from '../../Plans/Editor/contexts';
import Select from '../../../pro/components/Widgets/Select.react';
import { getPrimaryMeal, getMealAndLeftovers } from '../../../utils/Meals';
import { getAssetsForMeals } from '../../../utils/Meals';
import Analytics from '../../../utils/Analytics';

import './ConfirmOverwriteModal.scss';

export default class ConfirmOverwriteModal extends Component {

    static contextTypes = {
        isMobile: PropTypes.bool,
        meals: PropTypes.array,
    }

    static propTypes = {
        overlaps: PropTypes.array.isRequired,
        clears: PropTypes.array,
        mealTypes: PropTypes.array,
        meals: PropTypes.array,
        closeModal: PropTypes.func.isRequired,
        onModifyMeals: PropTypes.func.isRequired,
        logPastMeals: PropTypes.func,
        isRepeatMeal: PropTypes.bool,
        isRecipeSearch: PropTypes.bool,
        date: PropTypes.object,
        mealType: PropTypes.string,
        returnMode: PropTypes.bool,
        onReturnConflictData: PropTypes.func,
        additionalMealToAdd: PropTypes.object,
        additionalMealsToDelete: PropTypes.array
    };

    static defaultProps = {
        clears: [],
        mealTypes: [],
        meals: [],
        isRepeatMeal: false,
        isRecipeSearch: false
    };

    static childContextTypes = {
        recipes: PropTypes.object,
        foods: PropTypes.object,
    };

    constructor(props) {
        super(props);

        // Initialize overlaps with default 'replace' action and sort them
        const overlaps = props.overlaps
            .map(overlap => ({
                ...overlap,
                action: 'replace'
            }))
            .sort(this.sortOverlaps);

        this.state = {
            overlaps,
            recipes: {},
            foods: {},
            loading: true
        };
    }

    getChildContext = () => {
        const { recipes, foods } = this.state;
        return { recipes, foods };
    }

    componentDidMount = async () => {
        await this.loadMealAssets();
    }

    loadMealAssets = async () => {
        const { overlaps } = this.props;

        try {
            // Collect all meals that need asset loading
            const allMeals = overlaps.reduce((carry, overlap) => {
                return carry.concat(overlap.destMeals || []).concat(overlap.sourceMeals || []);
            }, []);

            const assets = await getAssetsForMeals(allMeals);
            
            this.setState({
                ...assets,
                loading: false
            });
        } catch (error) {
            console.error('Failed to load meal assets:', error);
            this.setState({ loading: false });
        }
    }

    getPrimaryMeal = (meals) => {
        const { recipes, foods } = this.state;
        return getPrimaryMeal(meals, recipes, foods);
    }

    setOverlapAction = (index, action) => {
        const { overlaps } = this.state;
        const overlap = overlaps[index];

        // Analytics tracking
        Analytics.confirmOverwriteModalChangeDropdown({
            "New value": action,
            "Old value": overlap.action,
            "Date": overlap.date.format("YYYY-MM-DD"),
            "Meal Type": overlap.mealType,
            "Source Meal UUIDs": overlap.sourceMeals?.map(meal => meal.uuid) || [],
            "Destination Meal UUIDs": overlap.destMeals?.map(meal => meal.uuid) || [],
        });

        // Update the action
        const updatedOverlaps = [...overlaps];
        updatedOverlaps[index] = { ...overlap, action };

        this.setState({ overlaps: updatedOverlaps });
    }

    processOverlaps = () => {
        const { overlaps } = this.state;
        const { meals: propsMeals = [], clears = [], date, mealType } = this.props;
        const { meals: allMeals = [] } = this.context;

        let dirtyMeals = [];
        let mealsToDelete = [];
        let leftoverDates = [];

        // Detect if user explicitly selected leftovers via LeftoverDatesModal
        // If any sourceMeals are leftovers, leftover modal was shown and user made selections
        const hasExplicitLeftoverSelection = overlaps.some(overlap =>
            overlap.sourceMeals?.some(meal => meal.meal_type === 'leftover')
        ) || clears.some(clear =>
            clear.sourceMeals?.some(meal => meal.meal_type === 'leftover')
        );

        // Collect all meal UUIDs that are explicitly being handled by this modal
        // These include both destMeals and sourceMeals from all overlaps
        const mealsHandledByModal = new Set();
        overlaps.forEach(overlap => {
            (overlap.destMeals || []).forEach(meal => mealsHandledByModal.add(meal.uuid));
            (overlap.sourceMeals || []).forEach(meal => mealsHandledByModal.add(meal.uuid));
        });

        // Process clears - convert them directly to dirtyMeals (no conflicts, just move to empty slots)
        clears.forEach(clear => {
            clear.sourceMeals?.forEach(meal => {
                // Deep copy and update destination info
                const newMeal = JSON.parse(JSON.stringify(meal));
                newMeal.meal = clear.mealType;
                newMeal.date = clear.date.format('YYYY-MM-DD');

                dirtyMeals.push(newMeal);
            });
        });

        // Process each overlap based on selected action
        overlaps.forEach((overlap, overlapIndex) => {
            const { action, destMeals = [], sourceMeals = [] } = overlap;

            if (action === 'swap') {
                // SWAP: Exchange positions between dest and source meals
                destMeals.forEach((meal, destIndex) => {
                    const updatedMeal = JSON.parse(JSON.stringify(meal)); // Deep copy
                    if (sourceMeals[0]) {
                        updatedMeal.meal = sourceMeals[0].meal;
                        updatedMeal.date = sourceMeals[0].date;
                    }
                    dirtyMeals.push(updatedMeal);
                });

            } else if (action === 'replace') {
                // REPLACE: Remove existing meals, add new ones
                destMeals.forEach(meal => {
                    if (!mealsToDelete.find(m => m.uuid === meal.uuid)) {
                        mealsToDelete.push(meal);
                    }
                });

            } else if (action === 'combine') {
                // COMBINE: Keep existing meals, just add new ones
                // No action needed for destMeals
            }

            // Process source meals
            sourceMeals.forEach((meal, mealIndex) => {
                if (meal.meal_type === 'leftover') {
                    // Handle leftover meals specially
                    if (!leftoverDates.find(({leftoverDate}) => leftoverDate.isSame(meal.date, 'day'))) {
                        leftoverDates.push({
                            leftoverDate: overlap.date,
                            mealType: overlap.mealType,
                            participants: overlap.participants || []
                        });
                    }

                    // Deep copy to avoid reference issues
                    const leftoverMeal = JSON.parse(JSON.stringify(meal));
                    leftoverMeal.meal = overlap.mealType;
                    leftoverMeal.date = overlap.date.format('YYYY-MM-DD');
                    
                    dirtyMeals.push(leftoverMeal);
                    return;
                }

                // Handle regular meals
                if (action === 'replace' || action === 'combine') {
                    // For replace/combine, delete the original source meal
                    if (!mealsToDelete.find(m => m.uuid === meal.uuid)) {
                        mealsToDelete.push({ ...meal });
                    }
                }

                // Create new meal for the destination
                const newMeal = JSON.parse(JSON.stringify(meal)); // Deep copy
                newMeal.meal = overlap.mealType;
                newMeal.date = overlap.date.format('YYYY-MM-DD');

                // Generate new UUID for replace/combine actions
                if (action === 'replace' || action === 'combine') {
                    const originalUuid = meal.uuid;  // Get from source meal, not copy
                    newMeal.uuid = uuid.v4();

                    // Find leftovers that belong to this original meal but are NOT being handled by the modal
                    // Use allMeals from context instead of meals from props to get complete dataset
                    // Only preserve unhandled leftovers if this is NOT an explicit leftover selection
                    // In explicit selection scenarios, only selected leftovers should exist
                    if (!hasExplicitLeftoverSelection) {
                        const mealAndLeftovers = getMealAndLeftovers(meal, allMeals);
                        const lo1 = mealAndLeftovers[1];

                        // If destination is after lo1's date, the main was dragged past lo1 — the leftover
                        // modal was shown (user selected dates or skipped). Skip re-linking so these
                        // leftovers are NOT added to dirtyMeals and the cascade can delete them.
                        if (!lo1 || !overlap.date.isAfter(lo1.date, 'day')) {
                            const unhandledLeftovers = allMeals.filter(m =>
                                m.parent_uuid === originalUuid &&
                                m.meal_type === 'leftover' &&
                                !mealsHandledByModal.has(m.uuid) &&
                                !dirtyMeals.find(d => d.uuid === m.uuid)
                            );

                            // Update these leftovers to point to the new meal UUID and add them to dirtyMeals
                            unhandledLeftovers.forEach(leftover => {
                                const updatedLeftover = JSON.parse(JSON.stringify(leftover));
                                updatedLeftover.parent_uuid = newMeal.uuid;
                                dirtyMeals.push(updatedLeftover);
                            });
                        }
                    }
                }

                dirtyMeals.push(newMeal);
            });
        });

        // make sure leftovers have correct parent uuid
        const uuidMapping = {};

        // Check overlaps for UUID changes
        overlaps.forEach(overlap => {
            overlap.sourceMeals?.forEach(sourceMeal => {
                if (sourceMeal.meal_type !== 'leftover') {
                    // Find the corresponding meal in dirtyMeals
                    const newMeal = dirtyMeals.find(meal =>
                        meal.meal_type !== 'leftover' &&
                        meal.recipe_uuid === sourceMeal.recipe_uuid &&
                        meal.food_uuid === sourceMeal.food_uuid
                    );
                    if (newMeal && newMeal.uuid !== sourceMeal.uuid) {
                        uuidMapping[sourceMeal.uuid] = newMeal.uuid;
                    }
                }
            });
        });

        // Check clears for UUID changes
        clears.forEach(clear => {
            clear.sourceMeals?.forEach(sourceMeal => {
                if (sourceMeal.meal_type !== 'leftover') {
                    // Find the corresponding meal in dirtyMeals
                    const newMeal = dirtyMeals.find(meal =>
                        meal.meal_type !== 'leftover' &&
                        meal.recipe_uuid === sourceMeal.recipe_uuid &&
                        meal.food_uuid === sourceMeal.food_uuid
                    );
                    if (newMeal && newMeal.uuid !== sourceMeal.uuid) {
                        uuidMapping[sourceMeal.uuid] = newMeal.uuid;
                    }
                }
            });
        });

        // Update leftover parent_uuids using the mapping
        dirtyMeals.forEach(leftover => {
            if (leftover.meal_type === 'leftover' && leftover.parent_uuid && uuidMapping[leftover.parent_uuid]) {
                leftover.parent_uuid = uuidMapping[leftover.parent_uuid];
            }
        });

        // Note: Original meals processing removed - overlap processing already handles moving source meals to destinations

        return { dirtyMeals, mealsToDelete, leftoverDates };
    }

    processConflictResolutions = () => {
        const { overlaps } = this.state;
        
        return overlaps.map((overlap, index) => {
            const action = overlap.action || 'replace'; // Default to replace if no action set
            
            const resolution = {
                conflictId: index,
                action: action,
                overlap: overlap
            };
            
            // Include additional data based on action type
            if (action === 'replace') {
                resolution.conflictingMeals = overlap.destMeals;
            } else if (action === 'move') {
                resolution.conflictingMeals = overlap.destMeals;
                resolution.newSlot = overlap.newSlot || null;
            } else if (action === 'cancel') {
                resolution.conflictingMeals = overlap.destMeals;
            }
            
            return resolution;
        });
    }

    onSave = () => {
        const { overlaps } = this.state;
        const { onModifyMeals, closeModal, logPastMeals, isRepeatMeal, recipes = {}, foods = {}, returnMode, onReturnConflictData } = this.props;
        
        // Return mode: send conflict resolution data back instead of applying directly
        if (returnMode && onReturnConflictData) {
            try {
                const conflictResolutions = this.processConflictResolutions();
                onReturnConflictData(conflictResolutions);
                closeModal(false);
                return;
            } catch (error) {
                console.error('Error processing conflict resolutions:', error);
                return;
            }
        }

        try {
            let { dirtyMeals, mealsToDelete, leftoverDates } = this.processOverlaps();
            const { additionalMealToAdd, additionalMealsToDelete } = this.props;

            // Add additional meal to dirty meals if provided (for swap flow main meal)
            if (additionalMealToAdd) {
                dirtyMeals = [additionalMealToAdd, ...dirtyMeals];
            }

            // Add additional meals to delete if provided (for swap flow replaceMeals)
            if (additionalMealsToDelete && additionalMealsToDelete.length > 0) {
                mealsToDelete = [...mealsToDelete, ...additionalMealsToDelete];
            }

            const getMealName = (meal) => {
                if (meal.meal_type === 'leftover') return `${meal.meal} leftover`;
                const { recipes: stateRecipes, foods: stateFoods } = this.state;
                const recipe = stateRecipes[meal.recipe_uuid];
                const food = stateFoods[meal.food_uuid];
                return recipe?.title || food?.title || 'Unknown meal';
            };

            // Apply past meal logging if provided
            if (logPastMeals) {
                dirtyMeals = logPastMeals(dirtyMeals);
            }

            // Execute the meal modifications
            // Note: Pass null for leftoverDates since this modal already creates leftover meals directly in processOverlaps
            // Skip automatic leftover recreation since we handle leftovers explicitly in processOverlaps
            onModifyMeals(dirtyMeals, mealsToDelete, null, false, true);

            // Analytics tracking
            Analytics.confirmOverwriteModalConfirm({
                "Actions": overlaps.map(overlap => overlap.action),
                "Dates": overlaps.map(overlap => overlap.date.format("YYYY-MM-DD")),
                "Meal Types": overlaps.map(overlap => overlap.mealType),
                "Source Meal UUIDs": overlaps.map(overlap =>
                    overlap.sourceMeals?.map(meal => meal.uuid) || []
                ),
                "Destination Meal UUIDs": overlaps.map(overlap =>
                    overlap.destMeals?.map(meal => meal.uuid) || []
                ),
            });

            closeModal(true);
        } catch (error) {
            console.error('Error processing overlaps:', error);
            // Could show an error message to user here
        }
    }

    onCancel = () => {
        const { closeModal } = this.props;
        Analytics.confirmOverwriteModalCancel();
        closeModal(false);
    }

    sortOverlaps = (a, b) => {
        const { mealTypes } = this.props;

        // Create meal type order
        const mealTypeOrder = mealTypes?.length > 0
            ? mealTypes.map(mt => mt.name || mt)
            : ['Breakfast', 'Lunch', 'Snack', 'Dinner'];

        // Sort by date first (compare only the date part, not time)
        if (!a.date.isSame(b.date, 'day')) {
            return a.date.valueOf() - b.date.valueOf();
        }

        // Then sort by meal type
        const aIndex = mealTypeOrder.indexOf(a.mealType);
        const bIndex = mealTypeOrder.indexOf(b.mealType);

        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;

        return aIndex - bIndex;
    }

    getActionOptions = () => {
        const { isRepeatMeal, isRecipeSearch, returnMode } = this.props;
        
        const actions = [
            {value: 'replace', label: 'REPLACE with'},
            {value: 'combine', label: 'COMBINE with'},
        ];

        // Add SWAP option for non-repeat, non-recipe-search scenarios, but not in return mode
        if (!isRepeatMeal && !isRecipeSearch && !returnMode) {
            actions.unshift({value: 'swap', label: 'SWAP with'});
        }

        return actions;
    }

    renderOverlapItem = (overlap, index) => {
        const { loading } = this.state;

        if (loading) {
            return <div key={index} className="overlap-loading">Loading...</div>;
        }

        const actionOptions = this.getActionOptions();

        return (
            <div className="overlap-display">
                <div className="overlap-display__header">
                    <h4 className="overlap-display__title">
                        {overlap.mealType}, {overlap.date.format('dddd, MMM Do')}
                    </h4>
                </div>

                <div className="overlap-display__content">
                    <div className="overlap-display__meals-horizontal">
                        <div className="overlap-display__meal-card">
                            <div className="overlap-display__meal-label">PREVIOUSLY SCHEDULED</div>
                            <MealPreviewCard meals={overlap.destMeals || []} />
                        </div>

                        <div className="overlap-display__action-center">
                            <Select
                                defaultClassName="el-select-container el-select-raspberry"
                                className="resolution-options"
                                options={actionOptions}
                                value={overlap.action}
                                onChange={action => this.setOverlapAction(index, action)}
                                placeholder="Select action..."
                            />
                        </div>

                        <div className="overlap-display__meal-card">
                            <div className="overlap-display__meal-label">NEW</div>
                            <MealPreviewCard meals={overlap.sourceMeals || []} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { overlaps, loading, recipes, foods } = this.state;
        const { closeModal } = this.props;

        if (loading) {
            return (
                <Modal
                    isOpen={true}
                    onRequestClose={this.onCancel}
                    closeModal={this.onCancel}
                    contentLabel="Loading overwrite confirmation"
                    className="log-portions-modal el-modal el-modal2"
                    overlayClassName="feed-modal-overlay log-portions-overlay"
                    closeTimeoutMS={250}
                >
                    <div className="confirm-overwrite-container-v2 el-modal-container el-modal2-container">
                        <div className="loading-state">Loading meal data...</div>
                    </div>
                </Modal>
            );
        }

        return (
            <Modal isOpen={true}
                onRequestClose={this.onCancel}
                closeModal={this.onCancel}
                contentLabel="Overwrite with leftovers"
                className="log-portions-modal el-modal el-modal2"
                overlayClassName="feed-modal-overlay log-portions-overlay"
                closeTimeoutMS={250}>
                <AssetsProvider assets={{ recipes, foods }}>
                    <div className="leftover-offsets-container confirm-overwrite-container-v2 el-modal-container el-modal2-container">
                        <header>
                            <button className="el-modal-close-x" onClick={this.onCancel}>
                                <i className="icon-close-x" />
                                <span className="assistive-text">Close Modal</span>
                            </button>

                            <h2>What should we do with your previously scheduled {overlaps.length > 1 ? 'meals' : 'meal'}?</h2>
                        </header>

                        <section className="confirm-overwrite-form preferences-form el-modal-body-container el-modal2-body-container el-form el-fonts">
                            <ul className="overlap-list">
                                {overlaps.map((overlap, index) => (
                                    <li key={index}>
                                        {this.renderOverlapItem(overlap, index)}
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <footer>
                            <button className="el-modal-cancel-btn" onClick={this.onCancel}>cancel</button>
                            <button className="el-modal-ok-btn" onClick={this.onSave}>Save</button>
                        </footer>
                    </div>
                </AssetsProvider>
            </Modal>
        );
    }
}